import React from 'react';
import {connect} from 'react-redux';
import ReactTable from 'react-table';
import { Link } from 'react-router-dom';
import _ from 'lodash';

import LANGUAGES_CONTROLLER from '../../../controllers/languages';
import {TableConfig} from '../../../utils/appConfig';
import ENUMS from '../../../utils/enums';
import NoData from '../../page/NoData';
import Pagination from '../../page/Pagination';
import FileSaveAs from '../../downloaders/FileSaveAs';
import VatBoxDetailsFilterPanel from './VatBoxDetailsFilterPanel';
import FormattedCurrencyValue from '../../fields/number/FormattedCurrencyValue';
import Logger, {LOG_LEVELS} from '../../../utils/log';
import { withFullStoryExclude } from '../../../utils/fullStoryHelper';

const log = new Logger('VAT_BOX_DETAILS', LOG_LEVELS.DEBUG);

const NoDataComponent = (props) => {
    return (
        <NoData noDataText="VATBoxDetails_NoData" {...props} />
    )
}

const currencyColumnOptions = {
    Cell: (row) => (
        <FormattedCurrencyValue value={row.value}></FormattedCurrencyValue>
    ),
    filterable: false,
    sortMethod: (a, b, desc) => {
        // force null and undefined to the bottom
        a = Number.parseFloat(a, 10);
        b = Number.parseFloat(b, 10);
        a = Number.isNaN(a) ? -Infinity : a;
        b = Number.isNaN(b) ? -Infinity : b;
        // Return either 1 or -1 to indicate a sort priority
        if (a > b) {
            return 1;
        }
        if (a < b) {
            return -1;
        }
        // returning 0 or undefined will use any subsequent column sorting methods or the row index as a tiebreaker
        return 0;
    },
    className: withFullStoryExclude("text-align-right"),
};

const DEFAULT_PAGINATION_CONFIG = {
    page: 0,
    pageSize: TableConfig.STANDARD_LEVEL_TWO_PAGE_SIZE,
    pageNumber: 1
};

export class VATBoxDetails extends React.Component {

    constructor(props) {
        super(props);
        //Bindings
        this.applyClickHandler = this.applyClickHandler.bind(this);
        this.fetchVATBoxDetails = this.fetchVATBoxDetails.bind(this);
        this.getCols = this.getCols.bind(this);
        this.getFileName = this.getFileName.bind(this);
        this.getDocumentId = this.getDocumentId.bind(this);
        this.fileSaveAsFetchContentHandler = this.fileSaveAsFetchContentHandler.bind(this);

        let {boxDetails={}} = this.props;
        this.state = {
            data: _.isArray(boxDetails.details) ? boxDetails.details: [],
            cols:  this.getCols(),
            pagination: Object.assign({}, DEFAULT_PAGINATION_CONFIG),
            sorted: [],
            loading: false,
            searchCriteria: {}
        };
    }

    fileSaveAsFetchContentHandler(){
        return this.props.onExportVatBoxDetailsCSV(Object.assign({}, this.state.searchCriteria))
     }

    getFileName() {
        log.debug('getFileName()');
        let {boxDetails={}} = this.props;
        if(_.isEmpty(boxDetails)) {
            return '';
        }

        let filingPeriod = (this.props.filingPeriod && ENUMS.getMonthNameFromNumber(Number(this.props.filingPeriod.toDate.substring(5, 7)))) || '';
        let toDate = (this.props.filingPeriod && this.props.filingPeriod.toDate.substring(0, 4)) || '';
        let boxName = typeof boxDetails.boxName === 'string' && boxDetails.boxName.length === 1 ? `0${boxDetails.boxName}` : boxDetails.boxName;
        let fileName = `${this.props.countryCode}-${filingPeriod}-${toDate}-Box-${boxName}`;
        log.debug('getFileName(): fileName: ', fileName);
        return fileName;
    }

    getDocumentId(invoiceNumber) {
        //from invoice number get document id
        let {boxDetails={}} = this.props;
        let details = _.isArray(boxDetails.details) ?  boxDetails.details : [];
        let filteredData = details.filter(data => data['invoiceNr'] === invoiceNumber);
        let documentId = _.map(filteredData, function (item) { return item.documentId; });
        if (documentId.length > 0) {
            return documentId[0];
        }
    }

    getCols() {
        let boxTotalValue = Number.parseFloat(this.props.totalValue);
        let totalValue = (Number.isNaN(boxTotalValue) ? 0.0 : boxTotalValue).toFixed(2);
        let {boxDetails={}} = this.props;
        let totalCount = Number.isNaN(boxDetails.totalBoxCount) ? 0: boxDetails.totalBoxCount;
        return [
            {
                Header: props => (LANGUAGES_CONTROLLER.format(this.props.languages, 'vatBoxDetails_InvoiceNumber')), 
                accessor: 'invoiceNr', 
                filterable: false,
                Cell: ({ original={} }) => {
                    //redirect to TransactionSearch page
                    let linkToUrl = '/TransactionSearch?exactMatch&reference=' + this.getDocumentId(original.invoiceNr) + '&searchType=1';
                    let linkComponent = (<Link to={linkToUrl}>{original.invoiceNr}</Link>)
                    return (
                        <div>
                            {linkComponent}
                        </div>
                    );
                }
            },
            { 
                Header: props => (LANGUAGES_CONTROLLER.format(this.props.languages, 'vatBoxDetails_Currency')), 
                accessor: 'currency', 
                filterable: false 
            },
            { 
                Header: props => (LANGUAGES_CONTROLLER.format(this.props.languages, 'vatBoxDetails_TotalExclVAT')), 
                accessor: 'exclVatValue', 
                ...currencyColumnOptions
            },
            {
                Header: props => (LANGUAGES_CONTROLLER.format(this.props.languages, 'vatBoxDetails_TotalValue')), accessor: 'totalValue',
                Footer: (
                    <div className={totalCount === 0 ? 'hide-react-table-footer': ''} style={{ width: '100%', textAlign: 'right' }}>
                        <strong>{LANGUAGES_CONTROLLER.format(this.props.languages, 'vatReturn_Drilldown_Total')}</strong>:
                    </div>
                )
                , ...currencyColumnOptions
            },
            {
                Header: props => (LANGUAGES_CONTROLLER.format(this.props.languages, 'vatBoxDetails_BoxValue')), accessor: 'boxValue',
                Footer: (
                    <div className={totalCount === 0 ? 'hide-react-table-footer': ''} style={{ width: '100%', textAlign: 'right' }}>
                        {
                            <FormattedCurrencyValue value={totalValue} />
                        }
                    </div>
                )
                , ...currencyColumnOptions
            },
            { Header: props => (LANGUAGES_CONTROLLER.format(this.props.languages, 'vatBoxDetails_VATCode')), accessor: 'vatCode', filterable: false, className: withFullStoryExclude() },
            { Header: props => (LANGUAGES_CONTROLLER.format(this.props.languages, 'vatBoxDetails_Currency2')), accessor: 'currency2', filterable: false },
            { Header: props => (LANGUAGES_CONTROLLER.format(this.props.languages, 'vatBoxDetails_TotalExclVAT2')), accessor: 'exclVatValue2', ...currencyColumnOptions },
            { Header: props => (LANGUAGES_CONTROLLER.format(this.props.languages, 'vatBoxDetails_TotalValue2')), accessor: 'totalValue2', ...currencyColumnOptions },
            { Header: props => (LANGUAGES_CONTROLLER.format(this.props.languages, 'vatBoxDetails_CompanyCode')), accessor: 'companyCode', filterable: false },
        ];
    }

    applyClickHandler(searchCriteria={}) {
        log.debug("applyClickHandler()");
        this.setState({
            searchCriteria: searchCriteria,
            sorted: []
        });
        if(this.viewTable && this.viewTable.state) {
            this.viewTable.state.sorted.splice(0, this.viewTable.state.sorted.length);
            this.viewTable.state.page = 0;
        }
        this.props.onApplyClick(Object.assign({}, DEFAULT_PAGINATION_CONFIG, {pageSize: this.state.pagination.pageSize}, searchCriteria));
    }

    fetchVATBoxDetails(tableState) {
        log.debug('fetchVATBoxDetails(): ');
        let { page, pageSize, sorted } = tableState;
        //If it's a sort request just return, as we don't need re-fetch the data
        if(_.isArray(sorted) && !_.isEmpty(sorted) && sorted !== this.state.sorted) {
          this.setState({
            sorted: tableState.sorted
          });
          return;
        }

        page = Object.is(page, NaN) ? 0 : page;
        pageSize = Object.is(pageSize, NaN) ? TableConfig.STANDARD_LEVEL_TWO_PAGE_SIZE : pageSize;
        let pagination = Object.assign({}, this.state.pagination, {
            pageNumber: page + 1,
            page: page,
            pageSize: pageSize,
            sorted: sorted
        });
        
        this.setState({
            pagination: pagination,
            sorted: []
        });
        
        //Only make request for non-sorting callbacks
        let requestData = _.isEqual(this.state.sorted, tableState.sorted);
        if(requestData) {
            this.props.fetchRequestHandler(Object.assign({}, this.state.searchCriteria, pagination));
        }
    }

    componentDidUpdate(prevProps) {
        log.debug("componentDidUpdate(): prevProps: ", prevProps);
        log.debug("componentDidUpdate(): props: ", this.props.boxDetails);
        if(this.props.boxDetails !== prevProps.boxDetails) {
            this.setState( {
                data: (this.props.boxDetails && this.props.boxDetails.details) || [],
                cols: this.getCols()
            });
        }
    }
    
    render() {
        log.debug("render(): ", this.props);
        let {boxDetails={}} = this.props;
        let data = _.isArray(this.state.data) ? this.state.data : [];
        let cols = this.state.cols || [];
        let pagination = this.state.pagination || Object.assign({}, DEFAULT_PAGINATION_CONFIG);
        //Hard-code value
        let totalCount = Number.isNaN(boxDetails.totalBoxCount) ? 0: boxDetails.totalBoxCount;
        let pages = totalCount === 0 || pagination.pageSize < 1 ? 1 : Math.ceil(boxDetails.totalBoxCount / pagination.pageSize);
        let header = `${LANGUAGES_CONTROLLER.format(this.props.languages, 'vatReturn_Drilldown_Box')}:${boxDetails.boxName || ''}`;
        let subHeaderKey =  boxDetails.boxType === undefined ? '' : (`${boxDetails.boxType}` === '0'? 'vatReturn_Drilldown_TaxableBasis': 'vatReturn_Drilldown_VAT'); 
        const fileName = this.getFileName();

        let sortedData = data;
        if (this.viewTable && this.viewTable.state && !_.isEmpty(this.viewTable.state.sorted)) {  
            this.viewTable.state.page = pagination.page;
            sortedData = _.orderBy(
              data,
              this.viewTable.state.sorted.map(sort => {
                return row => {
                  let value = row[sort.id]; 
                  if (value === null || value === null) {
                      return -Infinity;
                  }
                  return typeof value === "string" ?
                    value.toLowerCase() :
                    value;
                };
              }),
              this.viewTable.state.sorted.map(d => (d.desc ? "desc" : "asc"))
            );
        }

        return (
            <div data-test="component-vat-box-details">
                <h2 data-test="modal-header">{header}{' '}{_.isEmpty(subHeaderKey) ? '' : LANGUAGES_CONTROLLER.format(this.props.languages, subHeaderKey)}</h2>
                <VatBoxDetailsFilterPanel
                    onApplyClick={this.applyClickHandler}
                />
               <div>
                    <ReactTable
                        manual
                        pages={pages || 1}
                        defaultPageSize={TableConfig.PAGINATION.STANDARD_LEVEL_TWO_PAGE_SIZE}
                        pageSizeOptions={TableConfig.PAGINATION.STANDARD_PAGE_SIZE_OPTIONS}
                        minRows={0}
                        maxRows={0}
                        PaginationComponent={Pagination}
                        ref={ref => this.viewTable = ref}
                        data={sortedData}
                        columns={cols}
                        NoDataComponent={NoDataComponent}
                        onFetchData={this.fetchVATBoxDetails}
                        showPageJump={false}
                        showPagination={totalCount > 0}
                        className={totalCount === 0 ? 'hide-header hide-footer' : ''}
                    />
                </div>
                <br/>
                <FileSaveAs 
                    downloadLabelKey="vatBoxDetails_ExportToCSV" 
                    fetchContent={this.fileSaveAsFetchContentHandler}
                    filename={fileName}  
                    mimeType="text/csv"
                    charset="utf-8"
                    isBlob={true} 
                    disabled={totalCount === 0}
                />
            </div>
        );
    }
}


VATBoxDetails.defaultProps = {
    boxDetails: {},
    totalValue: 0.0,
    fetchRequestHandler: () => {},
    onApplyClick: () => {},
    onExportVatBoxDetailsCSV: () => {},
    loading: false
}

//React-redux access to state and dispatch
function mapStateToProps(state) {
    log.debug('mapStateToProps(): ');
    let stateProps = {};
    stateProps.languages = state.languages;
    log.debug('mapStateToProps(): stateProps: ', stateProps);
    return stateProps;
}

function mapDispatchersToProps(dispatch) {
    log.debug('mapDispatchersToProps(): ');
    let dispatcherProps = {};
    log.debug('mapDispatchersToProps(): dispatcherProps: ', dispatcherProps);
    return {dispatcherProps};
}

export default connect(mapStateToProps, mapDispatchersToProps)(VATBoxDetails);
